import * as React from 'react';
import {
  TFormFieldTypes,
  TFormFieldsMap,
  TFormFieldProps,
  TFormItemProps,
} from 'components/Form/FormItem/types';
import Checkbox from 'components/Checkbox';
import { Input, Password } from 'components/Input';

import PasswordProgress from 'components/PasswordProgress';
import Select from 'antd/lib/select';
import MultiInput from 'components/MultiInput/MultiInput';
import { ChangeEvent, useContext } from 'react';
import CustomMultiSelect from 'components/CustomMultiSelect/CustomMultiSelect';
import { FormInitialValuesContext } from '../Form';
import SFormItem from './style';

// A value counts as "meaningful" (i.e. a real default) when it's anything
// other than absent. 0 and false ARE considered meaningful because they're
// legitimate defaults (e.g. discount = 0, checkbox = false) — the user can
// submit without changing them, so the required asterisk would be noise.
// Only undefined/null/"" count as "needs to be filled in" by the user.
const hasMeaningfulValue = (value: unknown): boolean =>
  value !== undefined && value !== null && value !== '';

// Walk a nested path like ['address', 'street'] through an object to find the
// leaf value. Returns undefined if any step along the path is missing.
const resolvePath = (
  source: Record<string, unknown> | undefined,
  path: (string | number)[],
): unknown =>
  path.reduce<unknown>((current, key) => {
    if (current === undefined || current === null) return undefined;
    if (typeof current !== 'object') return undefined;
    return (current as Record<string | number, unknown>)[key];
  }, source);

const toNamePath = (
  name: string | number | (string | number)[] | undefined,
): (string | number)[] => {
  if (name === undefined) return [];
  if (Array.isArray(name)) return name;
  return [name];
};

const formFieldsMap: TFormFieldsMap = {
  [TFormFieldTypes.INPUT]: { Component: Input },
  [TFormFieldTypes.PASSWORD]: { Component: PasswordProgress },
  [TFormFieldTypes.POSSWORD_CONFIRM]: { Component: Password },
  [TFormFieldTypes.CHECKBOX]: { Component: Checkbox },
  [TFormFieldTypes.SELECT]: { Component: Select },
  [TFormFieldTypes.MULTIINPUT]: { Component: MultiInput, custom: true },
  [TFormFieldTypes.CUSTOM_MULTI_SELECT]: {
    Component: CustomMultiSelect,
    custom: true,
  },
};

export const FormField = (props: TFormFieldProps) => {
  const {
    fieldType,
    formItemLabel,
    title = '',
    label,
    name,
    selectOptions,
    rules,
    placeholder,
    onChange,
    setCustomFieldValue,
    valuePropName,
  } = props;
  if (!fieldType || !props) return null;
  const { Component, custom } = formFieldsMap[fieldType];

  const onHandleChange = (e: ChangeEvent<HTMLInputElement> | any) => {
    if (custom && setCustomFieldValue) {
      setCustomFieldValue(name as string, e);
    } else if (onChange) {
      onChange(e as ChangeEvent<HTMLInputElement>);
    }
  };
  return (
    <SFormItem
      rules={rules}
      name={name}
      valuePropName={valuePropName}
      label={formItemLabel}
    >
      <Component
        selectOptions={selectOptions}
        title={title}
        label={label}
        onChange={onHandleChange}
        placeholder={placeholder}
      />
    </SFormItem>
  );
};

export const FormItem = (props: TFormItemProps) => {
  const { initialValue, className, name } = props;
  const formInitialValues = useContext(FormInitialValuesContext);

  // Detect if this field has a non-empty default value, either from its own
  // `initialValue` prop or from the parent Form's `initialValues` prop.
  // If so, skip the required-field asterisk — the user can never submit it
  // empty, so marking it required visually is noise.
  const ownDefault = hasMeaningfulValue(initialValue);
  const namePath = toNamePath(name);
  const parentInitial = namePath.length
    ? resolvePath(formInitialValues, namePath)
    : undefined;
  const parentDefault = hasMeaningfulValue(parentInitial);
  const hasDefaultValue = ownDefault || parentDefault;

  const computedClassName = hasDefaultValue
    ? `${className || ''} has-default-value`.trim()
    : className;
  return <SFormItem {...props} className={computedClassName} />;
};
